import * as React from 'react'

import { Button } from '@factorialco/deprecated-design-system'

import EmployeeSelector from 'modules/core/components/EmployeeSelector'
import Modal from 'modules/core/components/Modal'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import usersCollection from 'modules/core/store/collections/users'

import employeesCollection from 'modules/employees/store/collections/employees'
import invitationsCollection from 'modules/employees/store/collections/invitations'
import type Employee from 'modules/employees/store/models/Employee'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'

interface Props {
  isHidden: boolean
  employees: Employee[]
  onClose: () => void
}

const NotInvitedModal = ({ isHidden, employees, onClose }: Props) => {
  const [selectedEmployeesIds, setSelectedEmployeesIds] = React.useState<number[]>([])

  useAsyncData(async () => {
    await policyAuthorizations.authorizePolicies('employee.invite', employees)
  }, [])

  if (isHidden) return null

  const handleClose = () => {
    setSelectedEmployeesIds([])
    onClose()
  }

  const disabledEmployeeIds = employees
    .filter((employee) => !policyAuthorizations.for('employee.invite', employee))
    .map((employee) => employee.id)

  const handleSubmit = async () => {
    const accessesIds = selectedEmployeesIds.map((x) => {
      const employee = employeesCollection.find({ id: x })
      return employee && employee.attributes.access_id
    })

    track(trackingEvents.employee.employeesSendInviteFinish)
    const { success } = await asyncAction(
      async () => {
        await invitationsCollection.rpc('bulk_invite', { accesses_ids: accessesIds })
        await usersCollection.fetch()
      },
      { loadingMessage: i18n.t('employees.not_invited_modal.loading_message') }
    )

    if (success) handleClose()
  }

  const handleSelectEmployee = (id: number | null | undefined) => {
    if (!id) return

    const nextSelectedEmployeeIds = selectedEmployeesIds.includes(id)
      ? selectedEmployeesIds.filter((x) => x !== id)
      : [...selectedEmployeesIds, id]

    setSelectedEmployeesIds(nextSelectedEmployeeIds)
  }

  return (
    <Modal
      title={i18n.t('employees.not_invited_modal.title')}
      description={i18n.t('employees.not_invited_modal.description')}
      illustration="group"
      onSubmit={handleSubmit}
      onClose={handleClose}
      footer={<Button submit>{i18n.t('employees.not_invited_modal.submit')}</Button>}
    >
      <EmployeeSelector
        employees={employees}
        onSelectEmployee={handleSelectEmployee}
        selectedEmployeeIds={selectedEmployeesIds}
        onSelectMultiple={setSelectedEmployeesIds}
        disabledEmployeeIds={disabledEmployeeIds}
      />
    </Modal>
  )
}

export default NotInvitedModal
